import { useStore } from '@/store';
import { createRouter, createWebHistory } from 'vue-router';
import InfoService from '@/services/infoService';

const infoService = new InfoService();

export function getFirstAccessibleRoute(perms: any) {
    // Order of preference. Adjust sequence as needed.
    const ordered = [
        { name: 'dashboard', allowed: () => perms?.hasAccessToDashboard },
        { name: 'order', allowed: () => perms?.hasAccessToOrderItemsTab },
        { name: 'cart', allowed: () => perms?.hasAccessToCartTab },
        { name: 'documents', allowed: () => perms?.hasAccessToDocumentsTab },
        { name: 'orders', allowed: () => perms?.hasAccessToPreviousOrdersTab },
        { name: 'downloads', allowed: () => perms?.hasAccessToDownload },
        { name: 'accountDebitors', allowed: () => perms?.hasAccessToMyDebitorsTab }
    ];
    const first = ordered.find(x => x.allowed());
    return first ? { name: first.name } : { name: 'loginForm' };
}

export function isRoutePermited(to: any, perms: any) {
    if (to.meta.requiresDashboardPermission && !perms?.hasAccessToDashboard) return false;
    if (to.meta.requiresOrderItemsPermission && !perms?.hasAccessToOrderItemsTab) return false;
    if (to.meta.requiresCartPermission && !perms?.hasAccessToCartTab) return false;
    if (to.meta.requiresDocumentsPermission && !perms?.hasAccessToDocumentsTab) return false;
    if (to.meta.requiresReOrderItemsPermission && !perms?.hasAccessToPreviousOrdersTab) return false;
    if (to.meta.requiresDownloadPermission && !perms?.hasAccessToDownload) return false;
    if (to.meta.requiresDebitorsPermission && !perms?.hasAccessToMyDebitorsTab) return false;
    return true;
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    linkActiveClass: 'active',
    linkExactActiveClass: 'exact-active',
    routes: [
        {
            path: '/',
            name: 'home',
            meta: { requiresAuth: true, requiresCustomer: true },
            component: () => import('@/views/DashboardView.vue')
        },
        {
            path: '/admin',
            name: 'admin',
            redirect: '/admin/impersonate-customer',
            meta: { requiresAuth: true, requiresAdmin: true },
            component: () => import('@/views/AdminView.vue'),
            children: [
                {
                    path: 'invite-users',
                    name: 'inviteUsers',
                    meta: { requiresAuth: true, requiresAdmin: true },
                    component: () => import('@/partials/AdminInviteUsers.vue')
                },
                {
                    path: 'impersonate-customer',
                    name: 'impersonateCustomer',
                    meta: { requiresAuth: true, requiresAdmin: true },
                    component: () => import('@/partials/AdminImpersonateCustomer.vue')
                }
            ]
        },
        {
            path: '/account',
            name: 'account',
            meta: { requiresAuth: true, requiresCustomer: true },
            component: () => import('@/views/AccountView.vue'),
            children: [
                {
                    path: '',
                    name: 'accountDetails',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/AccountDetailsPartial.vue')
                },
                {
                    path: 'change-password',
                    name: 'accountChangePassword',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/AccountChangePasswordPartial.vue')
                },
                {
                    path: 'debitors',
                    name: 'accountDebitors',
                    meta: { requiresAuth: true, requiresCustomer: true, requiresDebitorsPermission: true },
                    component: () => import('@/partials/AccountDebitorsPartial.vue')
                },
                {
                    path: 'debitors/edit/:id/:addressId',
                    name: 'accountDebitorsEdit',
                    meta: { requiresAuth: true, requiresCustomer: true, requiresDebitorsPermission: true },
                    component: () => import('@/partials/AccountDebitorsEditPartial.vue')
                },
                {
                    path: 'administrate-users',
                    name: 'administrateUsers',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/AdministrateCustomersRelatedToDebitors.vue')
                }
            ]
        },
        {
            path: '/cart',
            name: 'cart',
            meta: { requiresAuth: true, requiresCustomer: true, requiresCartPermission: true },
            component: () => import('@/views/CartView.vue')
        },
        {
            path: '/login',
            name: 'login',
            props: route => ({ query: route.query }),
            redirect: to => ({
                name: 'loginForm',
                query: to.query
            }),
            component: () => import('@/views/LoginView.vue'),
            children: [
                {
                    path: '',
                    name: 'loginForm',
                    props: route => ({ query: route.query }),
                    component: () => import('@/partials/LoginFormPartial.vue')
                },
                {
                    path: 'ad',
                    name: 'loginAd',
                    component: () => import('@/partials/LoginAdPartial.vue')
                },
                {
                    path: 'forgot-password',
                    name: 'forgotPassword',
                    component: () => import('@/partials/LoginForgotPasswordPartial.vue')
                },
                {
                    path: 'reset-password',
                    name: 'resetPassword',
                    component: () => import('@/partials/LoginConfirmPasswordPartial.vue')
                },
                {
                    path: 'choose-account',
                    name: 'chooseAccount',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/LoginChooseAccountPartial.vue')
                }
            ]
        },
        {
            path: '/dashboard',
            name: 'dashboard',
            meta: { requiresAuth: true, requiresCustomer: true, requiresDashboardPermission: true },
            component: () => import('@/views/DashboardView.vue')
        },
        {
            path: '/order',
            name: 'order',
            meta: { requiresAuth: true, requiresCustomer: true, requiresOrderItemsPermission: true },
            component: () => import('@/views/OrderView.vue')
        },
        {
            path: '/documents',
            name: 'documents',
            meta: { requiresAuth: true, requiresCustomer: true, requiresDocumentsPermission: true },
            component: () => import('@/views/DocumentsView.vue')
        },
        {
            path: '/orders',
            name: 'orders',
            meta: { requiresAuth: true, requiresCustomer: true, requiresReOrderItemsPermission: true },
            component: () => import('@/views/OrdersView.vue')
        },
        {
            path: '/download',
            name: 'downloads',
            meta: { requiresAuth: true, requiresCustomer: true, requiresDownloadPermission: true },
            component: () => import('@/views/DownloadView.vue')
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'notFound',
            // Empty component so router-view doesn't render anything for Umbraco pages.
            component: { render: () => null },
            meta: { requiresAuth: true, requiresCustomer: true }
        }
    ],
    scrollBehavior(to, from, savedPosition) {
        // Always scroll to top
        return { top: 0 };
    },
});

router.afterEach(() => {
    window.scrollTo(0, 0);
});

router.beforeEach(async (to) => {
    const store = useStore();

    // Allow choose-account unconditionally if they are on the login flow
    if (to.name === 'chooseAccount') {
        return true;
    }

    // 1. Authentication Check
    if (to.meta.requiresAuth) {
        if (!store.currentUser) {
            return {
                name: 'loginForm',
                query: {
                    ...to.query,
                    redirect: to.path !== '/' ? to.path : undefined
                }
            };
        }

        // ADD THIS: Validate against the server on every route change
        try {
            await infoService.Validate();
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                store.currentUser = undefined;
                store.currentDebitor = undefined;
                localStorage.removeItem("store");

                return {
                    name: 'loginForm',
                    query: { force: "true", redirect: to.path !== '/' ? to.path : undefined }
                };
            }
        }
    }

    // 2. Customer/Admin context check
    if (to.meta.requiresCustomer) {
        if (store.currentUser?.isAdmin) {
            return { name: 'admin' };
        }
        else if (!store.currentDebitor) {
            return { name: 'chooseAccount' };
        }
    }

    const perms = store.currentDebitor?.debitorPermissions;

    // 3. Dynamic Landing Page Decision
    // If navigating to "/", if the route is not found, or if the current route is not permitted, find the best home.
    if (to.name === 'notFound' || to.path === '/' || !isRoutePermited(to, perms)) {
        // We only force a redirect if we are at the root or if the current route is explicitly forbidden.
        if (to.name === 'notFound' && to.path !== '/') {
            return true;
        }
        return getFirstAccessibleRoute(perms);
    }
});

export default router;
